// src/api/features/account/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type { MyAccountResult, UpdateMyAccountBody } from "./types";

/* =========================
 * Helpers (API envelope)
 * ========================= */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: unknown): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

/* =========================
 * BASE URL
 * ========================= */

const BASE = PATHS.myaccount.base;

/* =========================
 * GET — /myaccount
 * ========================= */

/**
 * Fetch the logged-in user's own account/profile.
 * Backend resolves the user from the auth token/session.
 */
export async function getMyAccount(): Promise<MyAccountResult> {
  const res = await request<MyAccountResult | ApiEnvelope<MyAccountResult>>({
    method: "GET",
    url: BASE,
  });

  return unwrap<MyAccountResult>(res);
}

/* =========================
 * UPDATE — PUT /myaccount
 * ========================= */

/**
 * Update the logged-in user's own account/profile.
 *
 * Notes:
 * - We only send the fields from UpdateMyAccountBody.
 * - Basic string trimming is done here (in addition to UI-side mapping).
 */
export async function updateMyAccount(
  body: UpdateMyAccountBody
): Promise<MyAccountResult> {
  const dto: UpdateMyAccountBody = { ...body };

  // Trim common string fields defensively
  if (dto.firstName != null) dto.firstName = String(dto.firstName).trim();
  if (dto.lastName != null) dto.lastName = String(dto.lastName).trim();
  if (dto.username != null) dto.username = String(dto.username).trim();
  if (dto.email != null) dto.email = String(dto.email).trim();
  if (dto.phone != null) dto.phone = String(dto.phone).trim();
  if (dto.idNumber != null) dto.idNumber = String(dto.idNumber).trim();
  if (dto.addressLine1 != null) dto.addressLine1 = String(dto.addressLine1).trim();
  if (dto.addressLine2 != null) dto.addressLine2 = String(dto.addressLine2).trim();
  if (dto.country != null) dto.country = String(dto.country).trim();
  if (dto.state != null) dto.state = String(dto.state).trim();
  if (dto.city != null) dto.city = String(dto.city).trim();
  if (dto.avatarUrl != null) dto.avatarUrl = String(dto.avatarUrl).trim();

  const res = await request<MyAccountResult | ApiEnvelope<MyAccountResult>>({
    method: "PUT",
    url: BASE,
    data: dto,
  });

  return unwrap<MyAccountResult>(res);
}
